import { ComponentHealthCondition } from 'features/fde/assetHealthPlanning/componentHealthConditions'
import {
  AssetTrackerHiddenItemOpacity,
  generateSiteViewCheckerboardColor,
  generateSiteViewStripedColor,
  getBreakdownDisplayBackgroundColorWithOpacity,
  useSiteViewColors,
} from 'features/overview/utils/siteViewTheme'
import React, { FC, useEffect, useState } from 'react'
import { styled } from 'styled-components'
import { NoRLHealthScore } from 'types'

import { AssetTrackerBreakdown } from './assembleAssetTrackerBreakdown'

export enum BreakdownItemSelectionStatus {
  Selected,
  OtherSelected,
  NothingSelected,
}

const ScoreBreakDownDisplayItemContainer = styled.div<{
  $width: string
  $borderColor: string
  $selectionStatus: BreakdownItemSelectionStatus
}>`
  height: 100%;
  width: ${({ $width }) => $width};
  display: flex;
  flex-direction: row;
  border: 0.15rem solid ${({ $borderColor }) => $borderColor};
  opacity: ${({ $selectionStatus }) =>
    $selectionStatus === BreakdownItemSelectionStatus.OtherSelected
      ? AssetTrackerHiddenItemOpacity
      : 1};
  transition:
    width 0.5s ease-in-out,
    opacity 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
    transform: scale(1, 1.1);
  }
`

const BreakdownBarItem = styled.div<{
  $backgroundColor: string
  $width: string
}>`
  height: 100%;
  width: ${({ $width }) => $width};
  background: ${({ $backgroundColor }) => $backgroundColor};
`

export const ScoreBreakdownDisplayItem: FC<{
  id: string
  geckoCount: number
  sparseCount: number
  backgroundColor: string
  totalAssetCount: number
  applyFilter: () => void
  selectionStatus: BreakdownItemSelectionStatus
}> = (props) => {
  const [sparsePieceWidth, setSparsePieceWidth] = useState('0%')
  const [geckoPieceWidth, setGeckoPieceWidth] = useState('0%')
  const [totalWidth, setTotalWidth] = useState('0%')

  useEffect(() => {
    const thisTotalCount = props.geckoCount + props.sparseCount
    setGeckoPieceWidth(`${(props.geckoCount / thisTotalCount) * 100}%`)
    setSparsePieceWidth(`${(props.sparseCount / thisTotalCount) * 100}%`)
    setTotalWidth(`${(thisTotalCount / props.totalAssetCount) * 100}%`)
  }, [props.geckoCount, props.sparseCount, props.totalAssetCount])

  return (
    <ScoreBreakDownDisplayItemContainer
      $width={totalWidth}
      $borderColor={props.backgroundColor}
      $selectionStatus={props.selectionStatus}
      onClick={props.applyFilter}>
      {/*  Gecko Data Piece*/}
      <BreakdownBarItem
        id={`${props.id}-air`}
        $backgroundColor={props.backgroundColor}
        $width={geckoPieceWidth}
      />
      {/* Sparse Data Piece */}
      <BreakdownBarItem
        id={`${props.id}-traditional`}
        $backgroundColor={generateSiteViewStripedColor(props.backgroundColor)}
        $width={sparsePieceWidth}
      />
    </ScoreBreakDownDisplayItemContainer>
  )
}

/**
 * A single item in the breakdown bar that animates its width based on the itemPercent prop
 *
 * This is needed because the transition css property needs to go between 2 values, so this has it transition
 * from width=0 to width=itemPercent.
 */
const AnimatedWidthProgressBarItem: FC<{
  backgroundColor: string
  width: string
  id: string
}> = ({ backgroundColor, width, id }) => {
  const [displayWidth, setDisplayWidth] = useState('0%')

  useEffect(() => {
    setDisplayWidth(width)
  }, [width])

  return (
    <BreakdownBarItem
      id={id}
      $backgroundColor={backgroundColor}
      $width={displayWidth}
    />
  )
}

export const NotEnoughDataItem: FC<{
  width: string
  applyFilter: () => void
  selectionStatus: BreakdownItemSelectionStatus
}> = (props) => {
  const colors = useSiteViewColors()
  const baseColor = getBreakdownDisplayBackgroundColorWithOpacity(
    colors[NoRLHealthScore.NotEnoughData]
  )

  const backgroundColor = generateSiteViewCheckerboardColor(baseColor)

  return (
    <ScoreBreakDownDisplayItemContainer
      $width={props.width}
      $borderColor={baseColor}
      $selectionStatus={props.selectionStatus}
      onClick={props.applyFilter}>
      <AnimatedWidthProgressBarItem
        backgroundColor={backgroundColor}
        width={'100%'}
        id={'not-enough-data-item'}
      />
    </ScoreBreakDownDisplayItemContainer>
  )
}

export function generateHealthScoreDisplayItem(
  data: AssetTrackerBreakdown[ComponentHealthCondition],
  condition: ComponentHealthCondition,
  baseColor: string,
  totalAssetCount: number,
  selectionStatus: BreakdownItemSelectionStatus,
  applyFilter: () => void
) {
  if (!data.aiData && !data.traditionalData) {
    return null
  }

  const backgroundColor =
    getBreakdownDisplayBackgroundColorWithOpacity(baseColor)

  const id = `health-score-item-${condition}`

  return (
    <ScoreBreakdownDisplayItem
      id={id}
      key={id}
      geckoCount={data.aiData}
      sparseCount={data.traditionalData}
      backgroundColor={backgroundColor}
      totalAssetCount={totalAssetCount}
      applyFilter={applyFilter}
      selectionStatus={selectionStatus}
    />
  )
}
